import { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import { Button } from "./ui/button";

export function UserAvatar({ user, size = "h-9 w-9" }) {
  const [broken, setBroken] = useState(false);
  const cls = `${size} shrink-0 rounded-full`;
  if (user?.picture && !broken) {
    return <img src={user.picture} alt="" referrerPolicy="no-referrer" loading="lazy" className={`${cls} object-cover`} onError={() => setBroken(true)} data-testid="user-avatar-photo" />;
  }
  return <span className={`${cls} flex items-center justify-center bg-primary/15 text-primary text-sm font-bold`} data-testid="user-avatar-initial">{user?.name?.[0]?.toUpperCase()}</span>;
}

export function ProfilePhoto({ user, onChange, api, toast, errMsg }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/profile/photo", fd); await onChange(); toast.success("Photo updated"); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const remove = async () => { setBusy(true); try { await api.delete("/profile/photo"); await onChange(); toast.success("Photo removed"); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); } };
  return (
    <div className="flex items-center gap-4" data-testid="profile-photo-section">
      <UserAvatar user={user} size="h-16 w-16" />
      <div className="flex flex-wrap gap-2">
        <label className={`inline-flex h-9 cursor-pointer items-center rounded-md border px-3 text-sm font-medium hover:bg-muted transition-colors ${busy ? "pointer-events-none opacity-60" : ""}`} data-testid="profile-photo-upload-button">
          {busy ? "Uploading…" : user?.picture ? "Change photo" : "Upload photo"}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="sr-only" onChange={upload} data-testid="profile-photo-input" />
        </label>
        {user?.picture && <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={remove} data-testid="profile-photo-remove-button">Remove</Button>}
        <p className="w-full text-xs text-muted-foreground">JPG, PNG, WEBP or GIF, up to 5 MB. We crop it to a square.</p>
      </div>
    </div>
  );
}

export function ThemeToggle({ className = "" }) {
  const { theme, setTheme } = useTheme();
  return (
    <Button variant="ghost" size="icon" className={className} aria-label="Switch light or dark mode" title="Switch light or dark mode" data-testid="theme-toggle-button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
      <Sun className="h-4 w-4 dark:hidden" /><Moon className="h-4 w-4 hidden dark:block" />
    </Button>
  );
}

const Img = ({ src, className, priority }) => (
  <img src={src} alt="Rewardlance" width="207" height="56" className={className} decoding="async"
    loading={priority ? "eager" : "lazy"} fetchpriority={priority ? "high" : "auto"} />
);

export function Logo({ light, priority }) {
  return (
    <Link to="/" className="flex items-center shrink-0" data-testid="brand-logo" aria-label="Rewardlance home">
      {light ? <Img src="/logo-white.webp" className="h-14 w-auto" priority={priority} /> : (
        <>
          <Img src="/logo.webp" className="h-12 sm:h-14 w-auto dark:hidden" priority={priority} />
          <Img src="/logo-white.webp" className="h-12 sm:h-14 w-auto hidden dark:block" />
        </>
      )}
    </Link>
  );
}
